// El bloque «Documentos» del filtro de Personal (S02 R11, R12; P15-I19): sus opciones, sus
// tooltips, los parámetros que escribe en BaseGetsListado y lo que dice en «Filtro Aplicado».
//
// Módulo puro, para probarlo con Jest sin montar el modal (Periodo.select.vue). El modal lo usa
// sólo con la prop mostrarContratos, que únicamente enciende Personal/Listado.vue (E47).
//
// Los cinco parámetros viven en ColaboradoresGets_Model (Lib.V6Base) y el backend los filtra en
// ColaboradoresController.Contratos.cs (FiltraPorContrato). Sin Contratos.Listar, cualquiera de
// ellos con valor responde 403 "FiltroContrato" (PermisoFiltroContratoFilter, S10 R11): por eso el
// modal los quita cuando no muestra el bloque (R11, E74). La fecha de ingreso (P15-I114) vive aquí
// también, pero fuera del bloque y sin permiso (R25).
//
// Las etiquetas «Vigente», «Por vencer (30 días)», «Vencido» y «Sin contrato» son las del grupo
// ContratoEstados que devuelve Filtro/Listado (R10); «Cualquiera» y «Sin contrato vigente» las
// agrega el frontend. Las opciones de «Firma» y sus nombres vienen de S08 R20 (firma-contrato.js).

import { FILTRO_FIRMA, EstadosFirmaDeFiltro } from "@/util/firma-contrato";

// Clave del grupo de Filtro/Listado (R10) y nombre del parámetro (ColaboradoresController.GrupoContratoEstados).
export const GRUPO_CONTRATO_ESTADOS = "ContratoEstados";

// Los parámetros de contrato de ColaboradoresGets_Model, en el orden de PermisoFiltroContratoFilter.
// ContratoFechaModo (P15-I114, S02 R21) es parámetro de contrato: sin Contratos.Listar también da 403.
export const PARAMETROS_CONTRATO = Object.freeze([
  "ContratoEstados",
  "ContratoDesde",
  "ContratoHasta",
  "EstadosFirma",
  "ContratoFechaModo",
]);

// La fecha de ingreso (P15-I114, S02 R22, R25; D12.2) **no** es parámetro de contrato: no pide
// Contratos.Listar y se queda cuando el permiso falta (E117).
export const PARAMETROS_INGRESO = Object.freeze(["IngresoDesde", "IngresoHasta"]);

export const CUALQUIERA = "Cualquiera";
export const SIN_CONTRATO_VIGENTE = "SinContratoVigente";

// «Sin contrato vigente» no es el complemento de «Vigente» (Definiciones, D09.17): SinContrato + Vencido.
const ESTADOS_SIN_CONTRATO_VIGENTE = "SinContrato,Vencido";

// Tooltips de R11 (el de «Firma» es de S02; S08 R20 lo cita).
export const TIP_POR_VENCER = "El contrato más reciente ya empezó y termina entre hoy y dentro de 30 días";
export const TIP_SIN_CONTRATO_VIGENTE =
  "Personas sin contratos o cuyo contrato más reciente ya venció. No incluye a quien tiene hoy un contrato cancelado o con error, ni a quien espera una renovación que aún no empieza";
export const TIP_FIRMA =
  "Incluye a quien tenga algún contrato en ese estado, aunque no sea el más reciente. Si eliges fechas de contrato, ese mismo contrato debe caer en ellas.";
export const TIP_FECHAS = "Personas con algún contrato que cruce este rango, aunque empiece antes o termine después";
export const TIP_INICIO = "Personas con algún contrato que empiece entre estas fechas, ambas incluidas";
export const TIP_FIN =
  "Personas con algún contrato que termine entre estas fechas, ambas incluidas. No incluye contratos por tiempo indeterminado";
export const TIP_INGRESO = "Personas cuya fecha de ingreso cae entre estas fechas, ambas incluidas";
// El aviso ámbar de un modo sin fechas (maqueta-fechas.html, E112; aceptado por Mario, D12.6).
export const AVISO_MODO_SIN_FECHAS = "Elige las fechas: sin ellas no se filtra por fechas de contrato";

// Los modos de «Fechas de contrato» (P15-I114, S02 R24; D12.1), en el orden del selector. `valor` es
// el ContratoFechaModo del backend; «Cruza el rango» (Traslape) es el de siempre y no se manda.
export const MODO_TRASLAPE = "Traslape";
export const MODOS_FECHA = Object.freeze([
  Object.freeze({ valor: MODO_TRASLAPE, nombre: "Cruza el rango", tooltip: TIP_FECHAS, aplicado: "Contratos del" }),
  Object.freeze({ valor: "Inicio", nombre: "Inicia entre", tooltip: TIP_INICIO, aplicado: "Contratos que inician del" }),
  Object.freeze({ valor: "Fin", nombre: "Termina entre", tooltip: TIP_FIN, aplicado: "Contratos que terminan del" }),
]);

// El modo de un valor guardado, o «Cruza el rango» si no es uno de los tres.
function ModoDe(Valor) {
  return MODOS_FECHA.find((M) => M.valor === Valor) || MODOS_FECHA[0];
}

// El tooltip de «Fechas de contrato» y su ayuda, según el modo (R24).
export function TooltipModo(Valor) {
  return ModoDe(Valor).tooltip;
}

const TOOLTIPS_CONTRATO = Object.freeze({
  PorVencer: TIP_POR_VENCER,
  [SIN_CONTRATO_VIGENTE]: TIP_SIN_CONTRATO_VIGENTE,
});

const MESES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];

// El estado del bloque: «Contrato» y «Firma» en «Cualquiera», fechas vacías y «Cruza el rango» (E45, E111).
export function DocumentosVacios() {
  return { Contrato: CUALQUIERA, Firma: CUALQUIERA, Fechas: null, Modo: MODO_TRASLAPE };
}

// Copia, para que el estado guardado (PeriodoValoresGuarda) no comparta el arreglo de fechas con el modal.
export function CopiaDocumentos(Documentos) {
  const R = DocumentosVacios();
  if (Documentos == null) return R;
  if (Documentos.Contrato) R.Contrato = Documentos.Contrato;
  if (Documentos.Firma) R.Firma = Documentos.Firma;
  if (Array.isArray(Documentos.Fechas) && Documentos.Fechas.length == 2) R.Fechas = Documentos.Fechas.slice();
  R.Modo = ModoDe(Documentos.Modo).valor;
  return R;
}

export function TieneDocumentos(Documentos) {
  if (Documentos == null) return false;
  return (
    (Documentos.Contrato != null && Documentos.Contrato !== CUALQUIERA) ||
    (Documentos.Firma != null && Documentos.Firma !== CUALQUIERA) ||
    TieneFechas(Documentos.Fechas)
  );
}

function TieneFechas(Fechas) {
  return Array.isArray(Fechas) && Fechas.length == 2 && !!Fechas[0] && !!Fechas[1];
}

// ¿Es el grupo ContratoEstados de la respuesta de Filtro/Listado? (el id llega tal cual, R10)
export function EsGrupoContratos(Filtro) {
  return Filtro != null && typeof Filtro.id === "string" && Filtro.id.toLowerCase() === GRUPO_CONTRATO_ESTADOS.toLowerCase();
}

/**
 * Las opciones del control «Contrato»: «Cualquiera», los valores del grupo en su orden y con su
 * etiqueta, y «Sin contrato vigente».
 * @param {{ datos: Array<{ id: string, etiqueta: string }> }} Grupo el grupo ContratoEstados
 * @returns {Array<{ valor: string, nombre: string, tooltip?: string }>}
 */
export function OpcionesContrato(Grupo) {
  const R = [{ valor: CUALQUIERA, nombre: CUALQUIERA }];
  const Datos = Grupo != null && Array.isArray(Grupo.datos) ? Grupo.datos : [];
  Datos.forEach((D) => R.push(ConTooltip({ valor: D.id, nombre: D.etiqueta || D.id })));
  R.push(ConTooltip({ valor: SIN_CONTRATO_VIGENTE, nombre: "Sin contrato vigente" }));
  return R;
}

function ConTooltip(Opcion) {
  if (TOOLTIPS_CONTRATO[Opcion.valor]) Opcion.tooltip = TOOLTIPS_CONTRATO[Opcion.valor];
  return Opcion;
}

// Las opciones del control «Firma» (S08 R20): «Cualquiera», «Por firmar» y «Firmado».
export function OpcionesFirma() {
  return [{ valor: CUALQUIERA, nombre: CUALQUIERA }].concat(
    FILTRO_FIRMA.map((O) => ({ valor: O.valor, nombre: O.nombre }))
  );
}

// El valor de ContratoEstados para una opción del control «Contrato», o null («Cualquiera»).
export function EstadosContratoDeOpcion(Valor) {
  if (Valor == null || Valor === "" || Valor === CUALQUIERA) return null;
  if (Valor === SIN_CONTRATO_VIGENTE) return ESTADOS_SIN_CONTRATO_VIGENTE;
  return Valor;
}

// Quita los cinco parámetros de contrato (también ContratoFechaModo); deja la fecha de ingreso (E117).
// Devuelve true si había alguno.
export function QuitaParametrosContrato(Parametros) {
  if (Parametros == null) return false;
  let Habia = false;
  PARAMETROS_CONTRATO.forEach((P) => {
    if (Object.prototype.hasOwnProperty.call(Parametros, P)) {
      Habia = true;
      delete Parametros[P];
    }
  });
  return Habia;
}

/**
 * Escribe en `Parametros` (el BaseGetsListado que emite el modal) lo que pide el bloque. «Cualquiera»
 * y el rango vacío **borran** la llave: no se manda cadena vacía (plan, «Frontend»). El modo sale sólo
 * con fechas y si no es «Cruza el rango»: sin rango no se manda (E112) y con traslape la petición es la
 * de siempre (E111).
 */
export function AplicaDocumentos(Parametros, Documentos) {
  QuitaParametrosContrato(Parametros);
  if (Documentos == null) return Parametros;
  const Estados = EstadosContratoDeOpcion(Documentos.Contrato);
  if (Estados) Parametros.ContratoEstados = Estados;
  const Firmas = EstadosFirmaDeFiltro(Documentos.Firma);
  if (Firmas) Parametros.EstadosFirma = Firmas;
  if (TieneFechas(Documentos.Fechas)) {
    Parametros.ContratoDesde = Documentos.Fechas[0];
    Parametros.ContratoHasta = Documentos.Fechas[1];
    const Modo = ModoDe(Documentos.Modo).valor;
    if (Modo !== MODO_TRASLAPE) Parametros.ContratoFechaModo = Modo;
  }
  return Parametros;
}

// «2026-11-01» → «01/nov/2026», como los tooltips del indicador.
export function FechaCorta(Iso) {
  const M = /^(\d{4})-(\d{2})-(\d{2})/.exec(Iso || "");
  if (!M) return Iso || "";
  return `${M[3]}/${MESES[Number(M[2]) - 1]}/${M[1]}`;
}

/**
 * Lo que «Filtro Aplicado» dice del bloque (R12, E57): cada control que no está en «Cualquiera» o vacío.
 * @returns {string[]} p. ej. ["Contrato: Sin contrato vigente", "Firma: Por firmar", "Contratos del 01/nov/2026 al 30/nov/2026"]
 */
export function TextoDocumentos(Documentos, OpcionesDeContrato) {
  const R = [];
  if (Documentos == null) return R;
  if (Documentos.Contrato && Documentos.Contrato !== CUALQUIERA) {
    const O = (OpcionesDeContrato || []).find((X) => X.valor === Documentos.Contrato);
    R.push(`Contrato: ${O ? O.nombre : Documentos.Contrato}`);
  }
  if (Documentos.Firma && Documentos.Firma !== CUALQUIERA) {
    const O = OpcionesFirma().find((X) => X.valor === Documentos.Firma);
    R.push(`Firma: ${O ? O.nombre : Documentos.Firma}`);
  }
  if (TieneFechas(Documentos.Fechas))
    R.push(
      `${ModoDe(Documentos.Modo).aplicado} ${FechaCorta(Documentos.Fechas[0])} al ${FechaCorta(Documentos.Fechas[1])}`
    );
  return R;
}

// ---------------------------------------------------------------------------------------------
// «Fecha de ingreso» (P15-I114, S02 R22, R25, R26; D12.2, D12.5). Fuera del bloque: su estado no vive
// en Documentos (DocumentosVacios() no lo toca) y no depende de Contratos.Listar ni del grupo.
// ---------------------------------------------------------------------------------------------

// El rango de ingreso, o null si le falta un extremo (el daterange sólo entrega los dos días).
export function CopiaIngreso(Ingreso) {
  return TieneFechas(Ingreso) ? Ingreso.slice() : null;
}

// Quita IngresoDesde e IngresoHasta. Devuelve true si había alguno.
export function QuitaParametrosIngreso(Parametros) {
  if (Parametros == null) return false;
  let Habia = false;
  PARAMETROS_INGRESO.forEach((P) => {
    if (Object.prototype.hasOwnProperty.call(Parametros, P)) {
      Habia = true;
      delete Parametros[P];
    }
  });
  return Habia;
}

// Escribe IngresoDesde/IngresoHasta (YYYY-MM-DD) en `Parametros`; sin rango, borra las llaves.
export function AplicaIngreso(Parametros, Ingreso) {
  QuitaParametrosIngreso(Parametros);
  if (TieneFechas(Ingreso)) {
    Parametros.IngresoDesde = Ingreso[0];
    Parametros.IngresoHasta = Ingreso[1];
  }
  return Parametros;
}

// Lo que «Filtro Aplicado» dice del ingreso (R26): «Ingreso del 01/nov/2026 al 30/nov/2026», o "".
export function TextoIngreso(Ingreso) {
  if (!TieneFechas(Ingreso)) return "";
  return `Ingreso del ${FechaCorta(Ingreso[0])} al ${FechaCorta(Ingreso[1])}`;
}

export default {
  GRUPO_CONTRATO_ESTADOS,
  PARAMETROS_CONTRATO,
  PARAMETROS_INGRESO,
  CUALQUIERA,
  SIN_CONTRATO_VIGENTE,
  TIP_POR_VENCER,
  TIP_SIN_CONTRATO_VIGENTE,
  TIP_FIRMA,
  TIP_FECHAS,
  TIP_INICIO,
  TIP_FIN,
  TIP_INGRESO,
  AVISO_MODO_SIN_FECHAS,
  MODO_TRASLAPE,
  MODOS_FECHA,
  TooltipModo,
  DocumentosVacios,
  CopiaDocumentos,
  TieneDocumentos,
  EsGrupoContratos,
  OpcionesContrato,
  OpcionesFirma,
  EstadosContratoDeOpcion,
  QuitaParametrosContrato,
  AplicaDocumentos,
  FechaCorta,
  TextoDocumentos,
  CopiaIngreso,
  QuitaParametrosIngreso,
  AplicaIngreso,
  TextoIngreso,
};
